import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCatalogText } from '@/lib/localizeCatalog';
import type { Category } from '@/types';
import { useStyleMode } from '@/features/restaurant/useStyleMode';
import { categoryColor, onCategoryColor } from './categoryColor';

interface Props {
  categories: Category[];
  activeId: string | null;
  onPick: (id: string) => void;
  /** SC-730 bis : le repérage par couleur est réservé au carnet du serveur
   *  (voir `MenuPage`). Sans lui la colonne reprend le vert du thème sur la
   *  catégorie active, comme avant SC-730. */
  colored?: boolean;
  /** SC-1278 : carnet du serveur — noms français, comme les titres de `MenuPage`. */
  frenchOnly?: boolean;
}

export default function CategorySidebar({ categories, activeId, onPick, colored = false, frenchOnly = false }: Props) {
  const { t } = useTranslation();
  const ct = useCatalogText();
  const listRef = useRef<HTMLUListElement>(null);
  const premium = useStyleMode() === 'premium';

  useEffect(() => {
    if (!activeId || !listRef.current) return;
    const target = listRef.current.querySelector<HTMLButtonElement>(`[data-cat-id="${activeId}"]`);
    if (!target) return;
    const container = listRef.current;
    const targetTop = target.offsetTop;
    const targetBottom = target.offsetTop + target.offsetHeight;
    const visibleTop = container.scrollTop;
    const visibleBottom = container.scrollTop + container.clientHeight;
    if (targetBottom > visibleBottom - 12) {
      container.scrollTo({ top: targetBottom - container.clientHeight + 12, behavior: 'smooth' });
    } else if (targetTop < visibleTop + 12) {
      container.scrollTo({ top: targetTop - 12, behavior: 'smooth' });
    }
  }, [activeId]);

  // SC-1109 : la gouttiere de 22 px a gauche du nom ne sert que s'il y a
  // quelque chose a y mettre. Cote client aucune categorie n'a d'emoji ni de
  // couleur : elle restait vide et coutait 30 px a une colonne de 200 px —
  // « MENU CHIRASHI » sortait en « MENU CHIRA… ».
  const hasGutter = colored || categories.some((c) => c.emoji);

  return (
    /* SC-1109 : la colonne se colle sous l'en-tete MESURE (`--site-header-h`,
       publie par `SiteHeader`) et ne depasse jamais le bas de l'ecran. Le
       `top-[84px]` d'avant supposait un en-tete de 70 px ; avec le bandeau de
       precommande et le ruban il en fait 154, et le titre comme la premiere
       categorie passaient dessous sans qu'aucun defilement puisse les
       ramener. La hauteur est bornee sur la colonne entiere, pas sur la
       liste : le titre n'a plus a etre devine dans un calc. */
    <aside
      className="hidden lg:flex lg:flex-col sticky self-start"
      style={{
        top: 'calc(var(--site-header-h, 110px) + 14px)',
        maxHeight: 'calc(100vh - var(--site-header-h, 110px) - 28px)',
      }}
    >
      <h3 className="shrink-0 font-mono text-[11px] font-bold uppercase tracking-[0.08em] text-ink/60 mb-2 ms-1">
        {t('menu.categories')}
      </h3>
      <ul
        ref={listRef}
        className={`relative list-none p-0 m-0 ${
          premium ? 'border rounded-[12px]' : 'border-2'
        } border-[var(--border-color)] bg-paper min-h-0 overflow-y-auto overscroll-contain [scrollbar-width:thin]`}
      >
        {categories.map((c) => {
          const active = c.id === activeId;
          const color = colored ? categoryColor(c) : null;
          return (
            <li key={c.id} className="m-0">
              <button
                type="button"
                data-cat-id={c.id}
                onClick={() => onPick(c.id)}
                /* SC-1109 : sur un ecran peu haut (portable 1366×768, ou un
                   ecran plus grand mis a l'echelle 125 % par Windows) les
                   lignes se resserrent — 31 px au lieu de 41, soit un tiers de
                   categories en plus sans defiler. `pointer:fine` : une
                   tablette en paysage garde ses lignes de 41 px, 31 px sous un
                   doigt c'est la categorie voisine. */
                className={`relative w-full text-start ps-4 pe-3 py-2.5 [@media_(max-height:820px)_and_(pointer:fine)]:py-1.5 font-display text-[15px] [@media_(max-height:820px)_and_(pointer:fine)]:text-[14px] leading-tight border-b-[1px] border-ink/15 last:border-b-0 transition-colors flex items-center gap-2 ${
                  active
                    ? color
                      ? ''
                      : 'bg-mint text-[var(--on-primary)]'
                    : 'bg-paper text-ink hover:bg-cream'
                }`}
                style={
                  active && color
                    ? { backgroundColor: color, color: onCategoryColor(color) }
                    : undefined
                }
              >
                {active && (
                  <span aria-hidden="true" className="absolute start-0 top-0 bottom-0 w-[4px] bg-ink" />
                )}
                {/* SC-730 : la couleur de la catégorie occupe la gouttière de
                    l'emoji — qui n'est jamais renseigne cote catalogue — pour
                    donner ici le meme repere que les pastilles et les titres.
                    Cote client (sans couleur) la gouttiere reste vide : elle
                    garde l'alignement des noms des qu'UNE categorie a un
                    emoji ; si aucune n'en a, elle disparait (SC-1109). */}
                {hasGutter && (
                  <span className="w-[22px] shrink-0 flex items-center justify-center">
                    {c.emoji ? (
                      <span className="text-[18px] leading-none">{c.emoji}</span>
                    ) : color ? (
                      <span
                        aria-hidden="true"
                        className="block w-[14px] h-[14px] rounded-[4px] border border-ink/30"
                        style={{ backgroundColor: color }}
                      />
                    ) : null}
                  </span>
                )}
                {/* Deux lignes plutot qu'une ellipse : un nom coupe ne se
                    distingue plus de son voisin (« MENU CHIRA… »). */}
                <span className="min-w-0 line-clamp-2 break-words">{frenchOnly ? c.name : ct.name(c)}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
